import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import PondokIcon from "../components/PondokIcon";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const { status } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  if (status === "authed") return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      const msg = err.response?.data?.detail || "Gagal mengirim email";
      toast.error(typeof msg === "string" ? msg : "Gagal mengirim email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#FDFBF7]">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          data-testid="link-back-login"
          className="inline-flex items-center gap-2 text-sm text-[#586D61] hover:text-[#082618] mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> Kembali ke Login
        </Link>

        <div className="bg-white border border-[#EBE6DE] rounded-2xl p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-11 w-11 rounded-xl bg-[#D16B4D] flex items-center justify-center">
              <PondokIcon className="h-6 w-6 text-white" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-heading text-lg font-semibold text-[#172E21]">PondokRaja</div>
              <div className="text-xs text-[#586D61]">Lupa Password</div>
            </div>
          </div>

          {sent ? (
            <div data-testid="forgot-password-success" className="flex flex-col items-center text-center gap-4 py-4">
              <div className="h-16 w-16 rounded-full bg-[#2C7A51]/10 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-[#2C7A51]" />
              </div>
              <div>
                <h2 className="font-heading text-2xl text-[#172E21]">Email terkirim</h2>
                <p className="text-sm text-[#586D61] mt-2 leading-relaxed">
                  Jika email <b>{email}</b> terdaftar, kami sudah mengirim link reset password ke inbox Anda.
                  <br/>Link berlaku <b>30 menit</b>. Cek folder spam jika tidak terlihat.
                </p>
              </div>
              <Link
                to="/login"
                data-testid="link-back-to-login-after-sent"
                className="text-sm text-[#D16B4D] hover:underline font-medium mt-2"
              >
                Kembali ke halaman login
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-5">
              <div>
                <h1 className="font-heading text-3xl font-medium text-[#172E21]">Lupa Password?</h1>
                <p className="text-sm text-[#586D61] mt-1.5 leading-relaxed">
                  Masukkan email akun Anda. Kami akan kirim link reset password yang berlaku 30 menit.
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium">Email</Label>
                <div className="relative mt-1.5">
                  <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#586D61]" />
                  <Input
                    data-testid="input-forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@homestay.com"
                    className="pl-10 border-[#EBE6DE]"
                    required
                  />
                </div>
              </div>

              <Button
                data-testid="btn-forgot-submit"
                type="submit"
                disabled={loading}
                className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl py-6 mt-2"
              >
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Mengirim…</> : "Kirim Link Reset"}
              </Button>

              <div className="text-xs text-center text-[#586D61] pt-1">
                Ingat password Anda?{" "}
                <Link to="/login" className="text-[#D16B4D] hover:underline font-medium">Login</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
